import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/** Regions whose content comes from variant-level metafields. */
const VARIANT_REGIONS = ['.pdp-tabs__features', '.pdp-tabs__specs-body'];

/**
 * @extends {HTMLElement}
 */
class PdpTabs extends HTMLElement {
  connectedCallback() {
    this.#tabs = this.querySelectorAll('[data-pdp-tab]');
    this.#panels = this.querySelectorAll('[data-pdp-panel]');

    // The markup ships without aria-hidden/inert so the panels stay readable and
    // reachable when this script never runs. Apply the initial state here instead.
    const selected = Array.from(this.#tabs).find((tab) => tab.getAttribute('aria-selected') === 'true');
    const initial = selected ?? this.#tabs[0];

    if (initial) this.#activate(initial.dataset.pdpTab);

    this.#tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.#activate(tab.dataset.pdpTab));
      tab.addEventListener('keydown', (event) => this.#onKeydown(event, tab));
    });

    // The variant picker lives in a different section, so listen on the document.
    document.addEventListener(ThemeEvents.variantUpdate, this.#updateVariantRegions);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.variantUpdate, this.#updateVariantRegions);
  }

  /** @type {NodeListOf<HTMLButtonElement>} */
  #tabs;

  /** @type {NodeListOf<HTMLElement>} */
  #panels;

  /**
   * Swaps the features list and the spec table for the newly selected variant.
   *
   * Only the two variant-dependent regions are morphed — never the whole element —
   * so the open tab, its aria state and focus all survive the update.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateVariantRegions = (event) => {
    const { target } = event;

    // Product cards and quick-add dialogs share this event; they are not this product.
    if (target instanceof HTMLElement && target.closest('product-card, quick-add-dialog')) return;
    if (event.detail.data.productId !== this.dataset.productId) return;

    const newTabs = event.detail.data.html.querySelector('pdp-tabs');

    if (!newTabs) return;

    for (const selector of VARIANT_REGIONS) {
      const current = this.querySelector(selector);
      const updated = newTabs.querySelector(selector);

      if (current && updated) morph(current, updated, { childrenOnly: true });
    }
  };

  /**
   * @param {string} name
   */
  #activate(name) {
    this.#tabs.forEach((tab) => {
      const isActive = tab.dataset.pdpTab === name;
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
    });

    this.#panels.forEach((panel) => {
      const isActive = panel.dataset.pdpPanel === name;
      panel.classList.toggle('pdp-tabs__panel--inactive', !isActive);
      panel.setAttribute('aria-hidden', String(!isActive));
      if (isActive) {
        panel.removeAttribute('inert');
      } else {
        panel.setAttribute('inert', '');
      }
    });
  }

  /**
   * @param {KeyboardEvent} event
   * @param {HTMLButtonElement} tab
   */
  #onKeydown(event, tab) {
    const tabs = Array.from(this.#tabs);
    const index = tabs.indexOf(tab);
    let nextIndex = index;

    if (event.key === 'ArrowRight') {
      nextIndex = (index + 1) % tabs.length;
    } else if (event.key === 'ArrowLeft') {
      nextIndex = (index - 1 + tabs.length) % tabs.length;
    } else {
      return;
    }

    event.preventDefault();
    const nextTab = tabs[nextIndex];
    nextTab.focus();
    this.#activate(nextTab.dataset.pdpTab);
  }
}

if (!customElements.get('pdp-tabs')) {
  customElements.define('pdp-tabs', PdpTabs);
}
